import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { useGetListingsSearchesQuery, useImportNextListingsMutation } from "@/store/rest_api";

const BATCH_OPTIONS = [5, 10, 25, 50, 100];

export default function RecentPropertySearches() {
  const navigate = useNavigate();
  const { data: searches = [] } = useGetListingsSearchesQuery();
  const [importNext] = useImportNextListingsMutation();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [batchById, setBatchById] = useState<Record<number, number>>({});

  const batchFor = (id: number) => batchById[id] ?? 10;

  const handleImport = async (id: number) => {
    setBusyId(id);
    try {
      const result = await importNext({ id, batch: batchFor(id) }).unwrap();
      toast.success(`Imported ${result.importedCount} / ${result.totalRows} leads`);
    } catch {
      toast.error("Import failed");
    } finally {
      setBusyId(null);
    }
  };

  if (!searches.length) {
    return null;
  }

  return (
    <div className="space-y-2">
      <h2 className="text-lg font-medium">Recent searches</h2>
      <div className="rounded-md border divide-y">
        {searches.map((search) => (
          <div key={search.id} className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <div className="font-medium truncate">{search.label}</div>
              <div className="text-xs text-muted-foreground">
                {search.status === "BUILDING"
                  ? "Building..."
                  : search.status === "FAILED"
                    ? "Build failed"
                    : `Imported ${search.importedCount} / ${search.totalRows} leads`}
                {search.createdAt ? ` · ${new Date(search.createdAt).toLocaleDateString()}` : ""}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {search.importedCount > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/campaigns/new?searchId=${search.id}`)}
                >
                  <Megaphone className="mr-2 h-4 w-4" />
                  Start campaign
                </Button>
              )}
              {search.hasMore ? (
                <>
                  <Select
                    value={String(batchFor(search.id))}
                    onValueChange={(value) => setBatchById((prev) => ({ ...prev, [search.id]: Number(value) }))}
                  >
                    <SelectTrigger className="h-9 w-[88px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BATCH_OPTIONS.map((count) => (
                        <SelectItem key={count} value={String(count)}>{count}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" disabled={busyId === search.id} onClick={() => handleImport(search.id)}>
                    {busyId === search.id ? <Loader2 className="h-4 w-4 animate-spin" /> : `Import next ${batchFor(search.id)}`}
                  </Button>
                </>
              ) : search.status === "READY" ? (
                <Badge variant="secondary">All imported</Badge>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
